/**
 * O botão da tela final, lido de `config.acao`.
 *
 * **Quem manda é o banco.** A CHECK `no_acao_valida` (migração 0007) já recusa
 * `javascript:`, `data:`, `http://` e o endereço com espaço em branco no meio,
 * e nenhuma linha que viole isso existe na tabela. Este arquivo repete a regra
 * do lado de cá por dois motivos concretos, e nenhum deles é desconfiança do
 * SQL:
 *
 *  1. **O painel precisa dizer NÃO enquanto se digita.** Sem isso, o
 *     organizador escreve o endereço, sai do campo, e só descobre que o banco
 *     recusou quando a gravação falha com uma mensagem de constraint. O aviso
 *     tem de nascer no campo, junto do erro.
 *  2. **`href` não é texto.** O React escapa conteúdo, não esquema: um
 *     `href={valor}` com `javascript:` dentro executa. O portão que impede a
 *     tela de montar esse `href` mora aqui, e é chamado por quem renderiza —
 *     não pela boa vontade de quem escreve o componente.
 *
 * Os padrões abaixo são a tradução literal dos do 0007. Quando um mudar, o
 * outro muda junto — há teste comparando os dois lados nos mesmos casos.
 */

import type { AcaoDoFim, ConfigNo } from '../tipos';

/**
 * Os três esquemas que passam. Lista branca, nunca lista negra: `javascript:`,
 * `data:` e `vbscript:` são os três de que todo mundo se lembra, e o quarto é
 * o que passa.
 *
 * `http://` fica de fora mesmo sendo inerte — o convite viaja por rede que
 * ninguém controla, e um botão que degrada para texto claro é a mesma decisão
 * que o proxy do instalador já toma no ai-hub.
 */
export const ESQUEMAS_DA_ACAO = ['https://', 'mailto:', 'tel:'] as const;

/**
 * Nenhum espaço em branco em lugar nenhum: é o que fecha o clássico
 * `java<tab>script:`, que o navegador normaliza ANTES de olhar o esquema. Sem
 * espaço possível, a cadeia não pode começar por outra coisa.
 *
 * `\s` do JavaScript é mais largo que `[:space:]` do Postgres: ele pega
 * também o espaço inquebrável (U+00A0) e a marca de ordem de byte (U+FEFF).
 * Quando os dois discordam, o daqui recusa o que o banco aceitaria, e nunca o
 * contrário -- errar para o lado de recusar é o lado certo de errar.
 */
const HTTPS = /^https:\/\/[^\s<>"]+$/;
const MAILTO = /^mailto:[^\s<>"@]+@[^\s<>"]+$/;
const TEL = /^tel:\+?[0-9][0-9 ().-]*$/;

/** O mesmo teto do banco: `length(url) <= 2000`. */
const MAXIMO_DO_ENDERECO = 2000;
/** O mesmo teto do banco: `length(rotulo) <= 80`. */
export const MAXIMO_DO_ROTULO = 80;

/**
 * O endereço se ele for clicável, `null` se não for.
 *
 * Devolver `null` em vez de lançar é deliberado: quem chama é uma tela, e uma
 * tela que quebra por causa de um endereço mal digitado é pior que uma tela
 * sem botão.
 */
export function enderecoPermitido(bruto: unknown): string | null {
  if (typeof bruto !== 'string') return null;
  if (bruto.length === 0 || bruto.length > MAXIMO_DO_ENDERECO) return null;
  // Sem `trim`: o valor é comparado como está gravado, que é como o banco o
  // compara. Aparar aqui faria a tela aceitar o que o banco recusa, e a
  // gravação falharia depois de o campo já ter dito que estava bom.
  return HTTPS.test(bruto) || MAILTO.test(bruto) || TEL.test(bruto) ? bruto : null;
}

/** O motivo, escrito para quem está digitando. `null` quando não há motivo. */
export function motivoDoEnderecoRecusado(bruto: string): string | null {
  if (bruto.trim() === '') return null;
  if (enderecoPermitido(bruto) !== null) return null;
  if (bruto.length > MAXIMO_DO_ENDERECO) return 'Endereço longo demais.';
  if (/^http:\/\//i.test(bruto)) {
    return 'Endereço em http:// não entra: o convite viaja por rede que ninguém controla. Use https://.';
  }
  if (/\s/.test(bruto)) return 'O endereço não pode ter espaço.';
  return `O botão só aceita ${ESQUEMAS_DA_ACAO.join(', ')} — o resto é recusado pelo banco, porque um botão que roda script é o convidado quem clica.`;
}

/**
 * A ação de um `fim`, pronta para virar botão — ou `null`.
 *
 * Ela some inteira quando o endereço não passa: um botão com rótulo e sem
 * destino é pior que nenhum botão, porque o convidado clica e nada acontece.
 */
export function lerAcaoDoFim(config: ConfigNo | undefined): AcaoDoFim | null {
  const bruta = config?.['acao'];
  if (typeof bruta !== 'object' || bruta === null || Array.isArray(bruta)) return null;

  const registro = bruta as Record<string, unknown>;
  const rotulo = typeof registro['rotulo'] === 'string' ? registro['rotulo'].trim() : '';
  const url = enderecoPermitido(registro['url']);
  if (rotulo === '' || url === null) return null;

  return { rotulo, url };
}

/** O rótulo e o endereço como estão gravados, sem julgar — é o que o painel edita. */
export function lerAcaoCrua(config: ConfigNo | undefined): {
  readonly rotulo: string;
  readonly url: string;
} {
  const bruta = config?.['acao'];
  if (typeof bruta !== 'object' || bruta === null || Array.isArray(bruta)) {
    return { rotulo: '', url: '' };
  }
  const registro = bruta as Record<string, unknown>;
  return {
    rotulo: typeof registro['rotulo'] === 'string' ? registro['rotulo'] : '',
    url: typeof registro['url'] === 'string' ? registro['url'] : '',
  };
}

/**
 * `true` quando o endereço é o exemplo que um modelo deixou por completar.
 *
 * Enquanto isto for verdade, `validar_grafo` recusa a publicação com o motivo
 * escrito — é o que impede um formulário de sair com "Falar pelo WhatsApp"
 * apontando para número nenhum.
 */
export function acaoPendente(config: ConfigNo | undefined): boolean {
  const bruta = config?.['acao'];
  if (typeof bruta !== 'object' || bruta === null || Array.isArray(bruta)) return false;
  return (bruta as Record<string, unknown>)['pendente'] === true;
}

/**
 * O `config` com a ação atualizada — e **sem** a marca de pendente.
 *
 * Editar o botão é justamente o ato de completá-lo, então a marca sai junto.
 * Deixá-la para um segundo clique (um "confirmar que está pronto") seria
 * inventar um passo que só existe para o programa, não para quem organiza.
 *
 * Rótulo e endereço vazios apagam a chave inteira: `{"acao": {}}` é recusado
 * pelo banco, e um botão meio escrito não deve impedir de salvar o resto do nó.
 */
export function comAcao(
  config: ConfigNo,
  campos: { readonly rotulo: string; readonly url: string },
): ConfigNo {
  const { acao: _descartada, ...resto } = config as Record<string, unknown> & { acao?: unknown };

  if (campos.rotulo.trim() === '' && campos.url.trim() === '') return resto;

  return { ...resto, acao: { rotulo: campos.rotulo, url: campos.url } };
}

/**
 * O destino abre em aba nova? Só o que é navegação de página.
 *
 * `mailto:` e `tel:` entregam ao aplicativo do sistema e não trocam a página —
 * abrir aba para eles deixa uma aba em branco para o convidado fechar. `https:`
 * troca, e a aba nova é o que impede quem confirmou de perder a tela com as
 * instruções de chegada.
 */
export function abreEmAbaNova(url: string): boolean {
  return HTTPS.test(url);
}
